import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Plus, Edit, Trash2, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

export default function AdminCategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({ name: '', slug: '', description: '' });
  const navigate = useNavigate();

  useEffect(() => {
    const isAuth = sessionStorage.getItem('admin_auth');
    if (!isAuth) {
      navigate('/admin');
      return;
    }
    loadCategories();
  }, [navigate]);

  const loadCategories = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/journal/categories`);
      setCategories(await response.json());
    } catch (err) {
      toast.error('Failed to load categories');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const url = editingCategory
        ? `${backendUrl}/api/admin/journal/categories/${editingCategory.id}`
        : `${backendUrl}/api/admin/journal/categories`;
      const method = editingCategory ? 'PUT' : 'POST';
      
      const categoryData = {
        ...formData,
        id: editingCategory?.id || crypto.randomUUID(),
        created_at: editingCategory?.created_at || new Date().toISOString()
      };

      await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(categoryData)
      });

      toast.success(editingCategory ? 'Category updated' : 'Category created');
      setShowDialog(false);
      setEditingCategory(null);
      setFormData({ name: '', slug: '', description: '' });
      loadCategories();
    } catch (err) {
      toast.error('Failed to save category');
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({ name: category.name, slug: category.slug, description: category.description });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${backendUrl}/api/admin/journal/categories/${id}`, { method: 'DELETE' });
      toast.success('Category deleted');
      loadCategories();
    } catch (err) {
      toast.error('Failed to delete category');
    }
  };

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--bk-bg))] p-8">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/admin/journal">
              <Button variant="ghost"><ArrowLeft size={16} /></Button>
            </Link>
            <h1 className="font-display text-3xl text-[hsl(var(--bk-fg))]">Manage Categories</h1>
          </div>
          <Button onClick={() => setShowDialog(true)} className="bg-[hsl(var(--bk-accent))] text-[hsl(var(--bk-bg))]">
            <Plus size={16} className="mr-2" /> New Category
          </Button>
        </div>

        <div className="bg-[hsl(var(--bk-bg-elev))] border border-[hsl(var(--bk-line))] rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="border-b border-[hsl(var(--bk-line))]">
              <tr>
                <th className="text-left p-4 font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))]">NAME</th>
                <th className="text-left p-4 font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))]">SLUG</th>
                <th className="text-left p-4 font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))]">DESCRIPTION</th>
                <th className="text-right p-4 font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))]">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {categories.map(category => (
                <tr key={category.id} className="border-b border-[hsl(var(--bk-line))]">
                  <td className="p-4 font-body text-sm text-[hsl(var(--bk-fg))]">{category.name}</td>
                  <td className="p-4 font-mono-meta text-xs text-[hsl(var(--bk-fg-muted))]">{category.slug}</td>
                  <td className="p-4 font-body text-sm text-[hsl(var(--bk-fg-muted))] max-w-md truncate">{category.description}</td>
                  <td className="p-4 text-right">
                    <div className="flex gap-2 justify-end">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(category)}><Edit size={14} /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(category.id)}><Trash2 size={14} /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="bg-[hsl(var(--bk-bg-elev))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">{editingCategory ? 'Edit Category' : 'New Category'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))]">Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => {
                    const name = e.target.value;
                    setFormData({ ...formData, name, slug: generateSlug(name) });
                  }}
                  className="bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]"
                  required
                />
              </div>
              <div>
                <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))]">Slug</Label>
                <Input
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  className="bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]"
                  required
                />
              </div>
              <div>
                <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))]">Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]"
                />
              </div>
              <div className="flex gap-3 justify-end">
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
                <Button type="submit" className="bg-[hsl(var(--bk-accent))] text-[hsl(var(--bk-bg))]">Save</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
